import React, { useState, useMemo } from 'react';
import { SearchInput } from '@/components';
import { Button, Popover, Space, Select, Tag } from 'antd';
import { IoFunnelOutline, IoCloseOutline } from 'react-icons/io5';
import { REGION_FILTERS_OPTIONS } from '@/utils/constant/appConstant';
import { ACCOUNT_STATUS_OPTIONS } from '../constants';

interface OnboardingFiltersProps {
  filterStatus: number | null;
  filterRegion: number | null;
  searchQuery: string;
  onStatusChange: (value: number | null) => void;
  onRegionChange: (value: number | null) => void;
  onSearchChange: (value: string) => void;
  onApplyFilters: (filters: { status: number | null; region: number | null }) => void;
  totalRecords: number;
  filteredCount: number;
  statusCounts?: Array<{ statuscode: number; count: number }>;
  isRegionAll?: boolean;
  userRegion?: number;
}

export const OnboardingFilters: React.FC<OnboardingFiltersProps> = ({
  filterStatus,
  filterRegion,
  onStatusChange,
  onRegionChange,
  onSearchChange,
  onApplyFilters,
  searchQuery,
  statusCounts = [],
  isRegionAll = true,
  userRegion,
}) => {
  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);

  // Draft state — only committed when "Apply Filters" is clicked
  const [draftStatus, setDraftStatus] = useState<number | null>(filterStatus);
  const [draftRegion, setDraftRegion] = useState<number | null>(filterRegion);

  const handleOpenChange = (open: boolean) => {
    if (open) {
      setDraftStatus(filterStatus);
      setDraftRegion(filterRegion);
    }
    setFilterPopoverOpen(open);
  };

  const handleApplyFilters = () => {
    onApplyFilters({ status: draftStatus, region: draftRegion });
    setFilterPopoverOpen(false);
  };

  const handleClearAll = () => {
    setDraftStatus(null);
    setDraftRegion(null);
    onSearchChange('');
    onStatusChange(null);
    onRegionChange(isRegionAll ? null : (userRegion || null));
  };

  const getCountForStatus = (statusCode: number): number => {
    const statusCount = statusCounts.find((sc) => sc.statuscode === statusCode);
    return statusCount?.count || 0;
  };

  // Status options with counts
  const statusFilterOptions = useMemo(() => {
    return ACCOUNT_STATUS_OPTIONS.map((option) => ({
      value: option.value,
      label: `${option.label} (${getCountForStatus(option.value)})`,
    }));
  }, [statusCounts]);

  // Helper function to get filter label
  const getFilterLabel = (type: string, value: number | null) => {
    if (value === null) return null;
    switch (type) {
      case 'region':
        return REGION_FILTERS_OPTIONS.find(opt => opt.value === value)?.label;
      case 'status':
        return ACCOUNT_STATUS_OPTIONS.find(opt => opt.value === value)?.label;
      default:
        return null;
    }
  };

  // Count active applied filters
  const activeFilterCount = [
    filterStatus !== null,
    isRegionAll && filterRegion !== null,
  ].filter(Boolean).length;

  return (
    <div className="mb-4">
      <div className="flex gap-3 items-center flex-wrap">
        {/* Search */}
        <div className="flex-1 min-w-[250px] max-w-[500px]">
          <SearchInput
            placeholder="Search by company or email..."
            onChange={(e) => onSearchChange(e)}
            allowClear
          />
        </div>

        {/* More Filters Button */}
        <div>
          <Popover
            content={
              <div style={{ width: 380 }}>
                <div
                  style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: 8 }}
                  className="pr-2"
                >
                  <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    {/* Status Filter */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Status
                      </label>
                      <Select
                        style={{ width: '100%' }}
                        placeholder="Select status"
                        value={draftStatus === null ? undefined : draftStatus}
                        onChange={(value) => {
                          setDraftStatus(value === undefined || value === null ? null : value as number);
                        }}
                        options={statusFilterOptions}
                        allowClear
                      />
                    </div>

                    {/* Region Filter */}
                    {isRegionAll && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Region
                        </label>
                        <Select
                          style={{ width: '100%' }}
                          placeholder="Select region"
                          value={draftRegion === null ? undefined : draftRegion}
                          onChange={(value) => {
                            setDraftRegion(value === undefined || value === null ? null : value as number);
                          }}
                          options={REGION_FILTERS_OPTIONS}
                          allowClear
                        />
                      </div>
                    )}
                  </Space>
                </div>

                {/* Footer */}
                <div
                  className="pt-3 mt-3 flex flex-row justify-end"
                  style={{ borderTop: '1px solid #f0f0f0', position: 'sticky', bottom: 0, background: 'white' }}
                >
                  <Space style={{ width: '100%' }}>
                    {activeFilterCount > 0 && (
                      <Button block onClick={handleClearAll}>
                        Clear All
                      </Button>
                    )}
                    <Button type="primary" block onClick={handleApplyFilters}>
                      Apply Filters
                    </Button>
                  </Space>
                </div>
              </div>
            }
            title={
              <div className="flex items-center justify-between">
                <span>Filter Options</span>
                {activeFilterCount > 0 && (
                  <span className="text-xs text-gray-500">{activeFilterCount} active</span>
                )}
              </div>
            }
            trigger="click"
            open={filterPopoverOpen}
            onOpenChange={handleOpenChange}
            placement="bottomRight"
          >
            <Button icon={<IoFunnelOutline />} className='h-[44px]'>
              Filters
              {activeFilterCount > 0 && (
                <span className="ml-1 px-2 py-0.5 bg-primary-100 text-primary-600 rounded-full text-xs font-medium">
                  {activeFilterCount}
                </span>
              )}
            </Button>
          </Popover>
        </div>
      </div>

      {/* Active Filters Tags */}
      {activeFilterCount > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {filterStatus !== null && (
            <Tag closable color={"#dc3b41"} onClose={() => onStatusChange(null)}>
              Status: {getFilterLabel('status', filterStatus)}
            </Tag>
          )}
          {isRegionAll && filterRegion !== null && (
            <Tag closable color={"#dc3b41"} onClose={() => onRegionChange(null)}>
              Region: {getFilterLabel('region', filterRegion)}
            </Tag>
          )}
          <Button
            type="link"
            size="small"
            className='text-primary-500'
            icon={<IoCloseOutline />}
            onClick={handleClearAll}
          >
            Clear all
          </Button>
        </div>
      )}
    </div>
  );
};
